/**
 * Original POKEY-style tones for R.U.S.H.
 * Roy Glover wrote the M.U.L.E. theme and its effects. This is not that
 * recording or that melody. It is a short square-wave set in the same
 * spirit: a tick per step, a blip when the grant moves, a sting for each
 * colony event, and a quiet ostinato under the month.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'rush-sound-enabled';
const CHANGED = 'rush:sound-changed';

export type RushSound =
  | 'step'
  | 'grant'
  | 'claim'
  | 'tick'
  | 'trade'
  | 'solar_storm'
  | 'earth_eclipse'
  | 'dust_storm'
  | 'ice_discovery'
  | 'micrometeorite'
  | 'win';

type AudioCtor = typeof AudioContext;

let shared: AudioContext | null = null;
let musicTimer: number | null = null;
let musicOn = false;
let lastTickAt = 0;

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'true' : 'false');
  } catch {
    // The switch still updates this tab.
  }
  if (!on) stopMusic();
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: on }));
}

export function useSoundEnabled(): boolean {
  const [on, setOn] = useState(soundEnabled);
  useEffect(() => {
    const sync = () => setOn(soundEnabled());
    window.addEventListener(CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return on;
}

function ctor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || (window as Window & { webkitAudioContext?: AudioCtor }).webkitAudioContext || null;
}

function context(): AudioContext | null {
  if (!soundEnabled()) return null;
  const Ctx = ctor();
  if (!Ctx) return null;
  if (!shared) shared = new Ctx();
  if (shared.state === 'suspended') void shared.resume();
  return shared;
}

function beep(
  ctx: AudioContext,
  start: number,
  frequency: number,
  duration: number,
  type: OscillatorType,
  volume: number,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

function noise(ctx: AudioContext, start: number, duration: number, volume: number) {
  const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();
  source.buffer = buffer;
  filter.type = 'bandpass';
  filter.frequency.value = 900;
  gain.gain.setValueAtTime(volume, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(start);
  source.stop(start + duration);
}

function play(name: RushSound, ctx: AudioContext) {
  const now = ctx.currentTime;
  if (name === 'step') {
    noise(ctx, now, 0.035, 0.05);
    return;
  }
  if (name === 'grant') {
    beep(ctx, now, 740, 0.07, 'square', 0.05);
    return;
  }
  if (name === 'claim') {
    beep(ctx, now, 520, 0.08, 'square', 0.06);
    beep(ctx, now + 0.09, 780, 0.1, 'square', 0.06);
    return;
  }
  if (name === 'tick') {
    beep(ctx, now, 1320, 0.045, 'square', 0.04);
    return;
  }
  if (name === 'trade') {
    beep(ctx, now, 660, 0.07, 'square', 0.05);
    beep(ctx, now + 0.08, 880, 0.09, 'square', 0.05);
    return;
  }
  if (name === 'solar_storm') {
    [440, 554, 659, 880].forEach((frequency, index) => {
      beep(ctx, now + index * 0.07, frequency, 0.09, 'square', 0.05);
    });
    return;
  }
  if (name === 'earth_eclipse') {
    [392, 294, 196].forEach((frequency, index) => {
      beep(ctx, now + index * 0.1, frequency, 0.16, 'triangle', 0.07);
    });
    return;
  }
  if (name === 'dust_storm') {
    noise(ctx, now, 0.45, 0.08);
    return;
  }
  if (name === 'ice_discovery') {
    beep(ctx, now, 1046, 0.16, 'triangle', 0.06);
    beep(ctx, now + 0.12, 1318, 0.18, 'triangle', 0.05);
    return;
  }
  if (name === 'micrometeorite') {
    [0, 0.06, 0.11, 0.15].forEach((offset) => noise(ctx, now + offset, 0.04, 0.07));
    return;
  }
  if (name === 'win') {
    [523, 659, 784].forEach((frequency, index) => {
      beep(ctx, now + index * 0.14, frequency, 0.2, 'square', 0.06);
    });
  }
}

export function playSound(name: RushSound): void {
  const ctx = context();
  if (!ctx) return;
  const start = () => play(name, ctx);
  if (ctx.state === 'suspended') {
    ctx.resume().then(start).catch(() => {});
    return;
  }
  start();
}

export function playCountdown(seconds: number): void {
  if (seconds < 1 || seconds > 5) return;
  const now = Date.now();
  if (now - lastTickAt < 700) return;
  lastTickAt = now;
  playSound('tick');
}

const EVENT_SOUNDS: Array<[RegExp, RushSound]> = [
  [/solar storm/i, 'solar_storm'],
  [/eclipse/i, 'earth_eclipse'],
  [/dust storm/i, 'dust_storm'],
  [/ice discovery/i, 'ice_discovery'],
  [/micrometeorite/i, 'micrometeorite'],
];

export function playEventMessage(message: string): void {
  const match = EVENT_SOUNDS.find(([pattern]) => pattern.test(message));
  if (match) playSound(match[1]);
}

/** A minor pulse in A. It is not the M.U.L.E. title theme. */
export function ensureMusic(): void {
  if (!soundEnabled() || musicOn) return;
  const ctx = context();
  if (!ctx) return;
  musicOn = true;
  const notes = [220, 262, 294, 330];
  let step = 0;
  const tick = () => {
    if (!musicOn || !soundEnabled()) return;
    const live = context();
    if (!live) return;
    const frequency = notes[step % notes.length];
    beep(live, live.currentTime, frequency / 2, 0.16, 'square', 0.02);
    if (step % 8 === 0) beep(live, live.currentTime, frequency, 0.12, 'triangle', 0.015);
    step += 1;
    musicTimer = window.setTimeout(tick, 320);
  };
  tick();
}

export function stopMusic(): void {
  musicOn = false;
  if (musicTimer != null) {
    window.clearTimeout(musicTimer);
    musicTimer = null;
  }
}

export function unlockAudio(): void {
  const ctx = context();
  if (ctx && ctx.state === 'suspended') void ctx.resume();
}
